<template>
  <div class="container">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <h2 class="h2 card-header position-relative">{{ $t('Login') }}</h2>
          <div class="position-relative">
            <div v-show="loading" class="loader-splash">
              <loader-component/>
            </div>
            <div class="card-body" :class="{disabled: loading}">
              <form>
                <div class="row justify-content-center">
                  <BaseInput class="form-group col-md-6"
                             v-model.trim="formData.email"
                             type="email"
                             placeholder="Email"
                             autocomplete="username"
                             :error="errors.emailError"/>
                </div>
                <div class="row justify-content-center">
                  <BaseInput class="form-group col-md-6"
                             v-model.trim="formData.password"
                             type="password"
                             isPassword="true"
                             autocomplete="current-password"
                             :placeholder="$t('Password')"
                             :error="errors.passwordError"
                             @keyup.enter="login"
                             @enter="login"
                  />
                </div>
              </form>
              <div
                class="row justify-content-center align-items-center position-relative"
              >
                <button-component
                  class="btn login-btn m-4"
                  @btn-click="login"
                  :disabled="loading"
                >
                  {{ $t('Login') }}
                </button-component>
                <app-link
                  class="link m-4 pt-2"
                  :to="$options.PAGES.PASSWORD_RESET_REQUEST"
                >
                  {{ $t('Forgot Your Password?') }}
                </app-link>
              </div>
              <social-auth/>
              <div class="button-colors">
                <p> {{ $t('Don`t have an account yet?') }} </p>
                <app-link
                  class="link"
                  :to="$options.PAGES.REGISTRATION"
                >
                  {{ $t('Register') }}
                </app-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <adsense-block adClient="ca-pub-9623662250069302" adSlot="8527483633"/>

  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import {ERROR, USER_CAPABILITY} from '@/const';
import BaseInput from '../components/base-components/BaseInput';
import LoaderComponent from '../components/LoaderComponent';
import SocialAuth from '../components/base-components/SocialAuth';
import {ADD_POPUP, DO_LOGIN} from '@/store/actionTypes';
import {validateEmail} from '@/utils/helpers';
import AppLink from '@/components/base-components/AppLink';
import {REGISTRATION, HOME, PASSWORD_RESET_REQUEST} from '@/pages';
import AdsenseBlock from '@/components/base-components/adsense/AdsenseBlock';
import VisaicV3Analytics from '@/components/base-components/Analytics/VisaicV3Analytics';
import {GET_BASIC_PASS_SELECTED} from '@/store/getterTypes';
import ButtonComponent from '@/components/base-components/buttons/ButtonComponent.vue';

export default {
  name: 'LoginScreen',
  components: {
    ButtonComponent,
    AppLink,
    SocialAuth,
    LoaderComponent,
    BaseInput,
    AdsenseBlock,
  },
  data() {
    return {
      apiKey: this.$storage.get('api_key'),
      loading: false,
      additionalParamsToErrormessage: null,
      formData: {
        email: '',
        password: '',
      },
      errors: {
        emailError: '',
        passwordError: '',
      },
    };
  },
  PAGES: {
    REGISTRATION,
    HOME,
    PASSWORD_RESET_REQUEST,
  },
  computed: {
    ...mapState({
      channelSettings: (state) => state.channel.settings,
      capabilities: (state) => state.auth.capabilities,
    }),
    ...mapGetters({
      isBasicPassSelected: GET_BASIC_PASS_SELECTED,
    }),
  },
  methods: {
    ...mapActions({
      doLogin: DO_LOGIN,
      addPopup: ADD_POPUP,
    }),
    redirectToLastPage(silent) {
      if (silent) {
        // this will act's like location.reload after href change.
        // It will help to move to previous location and reload page
        // to remove adSense scripts from there avoiding
        // trigger and immediately aborting several requests
        const path = this.$storage.get('last_page') || this.$options.PAGES.HOME.path;
        const url = new URL(path, location.origin);

        if (this.$storage.get('last_page') && this.$storage.get('last_page_query')) {
          Object.entries(this.$storage.get('last_page_query')).forEach(([key, val = '']) => {
            url.searchParams.append(key, val);
          });
        }
        location.href = url.href;
        return;
      }
      this.$storage.get('last_page') ?
        this.$router.replace({
          path: this.$storage.get('last_page'),
          query: this.$storage.get('last_page_query'),
        }) :
        this.$router.push(this.$options.PAGES.HOME);
    },
    onSuccessLogin() {
      VisaicV3Analytics.loginEvent(this.formData.email);
      this.clearErrors();
      if (this.isBasicPassSelected) {
        this.redirectToLastPage(this.capabilities[USER_CAPABILITY.no_ads]);
      }
      this.loading = false;
    },
    login() {
      if (this.loading) return;

      this.clearErrors();
      if (!this.formData.email || !this.formData.password.length) {
        this.loading = false;
        !this.formData.email ?
          this.errors.emailError = ERROR.ENTER_EMAIL :
          null;
        !this.formData.password.length ?
          this.errors.passwordError = ERROR.SHORT_PASSWORD :
          null;
        return;
      }
      if (!validateEmail(this.formData.email)) {
        this.errors.emailError = ERROR.WRONG_EMAIL;
        return;
      }
      this.loading = true;
      this.doLogin({
        email: this.formData.email,
        password: this.formData.password,
      }).then(this.onSuccessLogin).catch((data) => {
        if (data?.message) {
          if (data.message === ERROR.WRONG_AUTH_DATA) {
            this.errors.passwordError = ERROR.WRONG_PASSWORD;
          }
          if (data.message === ERROR.ALREADY_LOGGED) {
            this.errors.passwordError = ERROR.ALREADY_LOGGED;
            this.onSuccessLogin();
          }
        }
        this.loading = false;
        if (data.error?.errors?.terms) {
          this.showPopup(this.formData.email, this.formData.password);
          return;
        }
      });
    },
    clearErrors() {
      this.errors.emailError = '';
      this.errors.passwordError = '';
    },
    showPopup(email, password) {
      this.addPopup({
        component: './TermsAcceptPopup.vue',
      }).then(() => {
        this.doLogin({
          email,
          password,
          terms: true,
        }).then(this.onSuccessLogin);
      }).catch((reason) => {
        console.log('reason', reason);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/css/colors";

.container {
  margin: 9.5rem auto;
}

.link {
  color: $link-accent;
}

.loader-splash {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--settings-body-text-color-5);
  backdrop-filter: blur(.25rem);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-btn {
  background-color: var(--settings-login-button-background);
  color: var(--settings-login-button-text-color);
  position: relative;
  z-index: 1;
  border-radius: .3rem;
  font-weight: normal;
  text-transform: none;

  &:before {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    content: '';
    background-color: var(--settings-login-button-background);
    transition: all .2s linear;
    border-radius: .3rem;
  }

  &:hover {
    background-color: var(--settings-login-button-background);
    color: var(--settings-login-button-text-color);
    box-shadow: none;

    &:before {
      filter: blur(3px);
      opacity: .5;
      transform: translate(3px, 3px);
    }
  }
}

.card-body.disabled {
  pointer-events: none;
  filter: var(--disable-filter);
}
</style>
